import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit3, Filter, ChevronDown, ChevronUp, Eye, Search } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';
import UniversalList from '../components/UniversalList';

const PAGE_SIZE_DEFAULT = 20;

const AttributeSetPage = () => {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canAdd = hasPermission('settings_attribute_sets', 'add');
  const canEdit = hasPermission('settings_attribute_sets', 'edit');
  const canDelete = hasPermission('settings_attribute_sets', 'delete');
  const canView = hasPermission('settings_attribute_sets', 'view');
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const filterRef = useRef(null);

  useEffect(() => {
    fetchSets();
  }, []);

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset page on search/filter change
  useEffect(() => { setPage(1); }, [searchTerm, filterEntityType]);

  const fetchSets = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/settings/attribute-sets');
      const data = await res.json();
      if (data.status === 'success') {
        setSets(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch attribute sets:', err);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filter
  const filteredSets = useMemo(() => {
    return sets.filter(s => {
      if (filterEntityType !== 'all' && s.entity_type !== filterEntityType) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const code = (s.set_code || '').toLowerCase();
        const name = (s.set_name || '').toLowerCase();
        const entity = (s.entity_type || '').toLowerCase();
        if (!code.includes(q) && !name.includes(q) && !entity.includes(q)) return false;
      }
      return true;
    });
  }, [sets, filterEntityType, searchTerm]);

  // Client-side paginate
  const totalItems = filteredSets.length;
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSets.slice(start, start + pageSize);
  }, [filteredSets, page, pageSize]);

  const handleDelete = async (setId, setCode, isSystem) => {
    if (isSystem == 1 || setCode === 'default') {
      if (isSystem == 1) {
        notify('System attribute sets cannot be deleted.', 'success');
      } else {
        notify(t('common.default_set_cannot_delete'), 'success');
      }
      return;
    }
    if (!window.confirm(t('common.delete_confirm_set'))) return;
    try {
      const res = await fetch(`/api/settings/attribute-sets/${setId}/delete`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchSets();
      } else {
        notify(t('common.delete_failed', {msg: data.message}), 'error');
      }
    } catch (err) {
      console.error('Failed to delete attribute set:', err);
      notify(t('common.delete_network_error'), 'error');
    }
  };

  const getEntityTypeLabel = (type) => {
    switch (type) {
      case 'product': return t('components.dynamic_eav_form.product');
      case 'supplier': return t('components.dynamic_eav_form.supplier');
      case 'employee': return t('components.dynamic_eav_form.employee');
      default: return type;
    }
  };

  const entityTypeOptions = [
    { key: 'all', label: t('eav.all_entity_types') },
    { key: 'product', label: t('eav.product_attribute_sets') },
    { key: 'supplier', label: t('eav.supplier_attribute_sets') },
    { key: 'employee', label: t('eav.employee_attribute_sets') },
  ];

  const filterLabel = filterEntityType === 'all'
    ? t('eav.filter_button')
    : getEntityTypeLabel(filterEntityType);

  const columns = [
    {
      key: 'entity_type',
      label: t('eav.entity_type'),
      render: (val) => (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
          {getEntityTypeLabel(val)}
        </span>
      ),
    },
    {
      key: 'set_code',
      label: t('eav.set_code'),
      className: 'font-mono text-sm',
    },
    {
      key: 'set_name',
      label: t('eav.set_name'),
      render: (val, row) => (
        <div className="flex items-center gap-2">
          <span>{val}</span>
          {row.is_system == 1 && (
            <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded">SYSTEM</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: t('eav.actions'),
      headerClassName: 'text-right',
      className: 'text-right',
      render: (val, row) => (
        <div className="flex gap-1 items-center justify-end">
          {canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/attribute-sets/edit/${row.set_id}`); }}
              className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              title={t('eav.edit')}
            >
              <Edit3 size={16} />
            </button>
          )}
          {canView && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/attribute-sets/view/${row.set_id}`); }}
              className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
              title={t('common.view')}
            >
              <Eye size={16} />
            </button>
          )}
          {canDelete && (
            row.is_system == 1 || row.set_code === 'default' ? (
            <span
              className="p-1.5 text-slate-300 dark:text-slate-600 cursor-not-allowed inline-block"
              title={row.is_system == 1 ? 'System attribute set cannot be deleted' : 'Default attribute set cannot be deleted'}
            >
              <Trash2 size={16} />
            </span>
          ) : (
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(row.set_id, row.set_code, row.is_system); }}
                className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title={t('eav.delete')}
              >
                <Trash2 size={16} />
              </button>
            ))}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold">
          {t('eav.title')}
        </h1>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="md:hidden" />
        <div className="flex flex-wrap gap-3 md:justify-end">
          {canAdd && (
            <button
              onClick={() => navigate('/attribute-sets/new')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              {t('common.add')}
            </button>
          )}
        </div>
      </div>

      {/* Search + Filter bar */}
      <div className="relative">
        <div className="flex flex-col md:flex-row flex-wrap gap-3">
          <div className="relative w-full md:flex-1 md:min-w-[200px] md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('eav.search_attribute_sets')}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white text-sm"
            />
          </div>
          <div className="relative w-full md:w-auto" ref={filterRef}>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full md:w-auto flex items-center justify-center md:justify-start gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white"
            >
              <Filter size={16} />
              {filterLabel}
              {isFilterOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {isFilterOpen && (
              <div className="absolute left-0 top-full mt-1 w-full md:w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-[9999] p-2 animate-in fade-in slide-in-from-top-2 overflow-visible">
                <div className="space-y-1">
                  {entityTypeOptions.map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => { setFilterEntityType(opt.key); setIsFilterOpen(false); }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        filterEntityType === opt.key
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <UniversalList
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        columns={columns}
        data={pageData}
        loading={loading}
        emptyMessage={t('eav.no_sets')}
        rowKey="set_id"
        page={page}
        totalItems={totalItems}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        onRowClick={(row) => navigate(`/attribute-sets/view/${row.set_id}`)}
      />
    </div>
  );
};

export default AttributeSetPage;
